import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Clock, MapPin, AlertTriangle } from "lucide-react";
import PhotoUploader, { PhotoGallery } from "@/components/PhotoUploader";
import MaterialsList from "@/components/MaterialsList";
import SignatureCompleteModal from "@/components/SignatureCompleteModal";

export default function EmployeeWorkOrders() {
  const [params] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [logText, setLogText] = useState("");
  const [hours, setHours] = useState(0);
  const [pos, setPos] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [showSig, setShowSig] = useState(false);

  const wantId = params.get("id");
  const load = useCallback(async () => {
    try {
      const r = await api.get("/work-orders");
      setWorkOrders(r.data);
      if (wantId) {
        const match = (r.data || []).find((w) => w.work_order_id === wantId);
        if (match) setActive(match);
      }
    } catch (err) {
      console.error("Failed to load work orders:", err);
    }
  }, [wantId]);

  useEffect(() => { load(); }, [load]);

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success("Location captured");
      },
      () => toast.error("Could not get location"),
    );
  };

  const submitLog = async () => {
    if (!active || !logText) return;
    try {
      await api.post(`/work-orders/${active.work_order_id}/log`, {
        text: logText,
        hours: parseFloat(hours) || 0,
        lat: pos?.lat,
        lng: pos?.lng,
        photos,
      });
      toast.success("Log submitted");
      setLogText("");
      setHours(0);
      setPhotos([]);
      load();
    } catch {
      toast.error("Could not log your hours. Check your connection and try again.");
    }
  };

  const markComplete = async () => {
    if (!active) return;
    setShowSig(true);
  };

  return (
    <div data-testid="employee-work-orders">
      <p className="overline">Field work</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2 mb-8">My Work Orders</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          {workOrders.map((wo) => (
            <button
              key={wo.work_order_id}
              onClick={() => setActive(wo)}
              data-testid={`emp-wo-${wo.work_order_id}`}
              className={`w-full text-left card-pb p-4 transition-all ${active?.work_order_id === wo.work_order_id ? "border-[#C9A356] border-2" : "hover:-translate-y-0.5"}`}
            >
              <div className="flex justify-between items-start">
                <div className="font-medium text-[#0A2540]">{wo.title}</div>
                <span className={`text-xs uppercase font-bold px-2 py-0.5 rounded-full ${
                  wo.status === "completed" ? "bg-emerald-100 text-emerald-700" : wo.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                }`}>{wo.status?.replace(/_/g, " ")}</span>
              </div>
              <div className="text-xs text-[#475569] mt-1">{wo.customer_code}</div>
            </button>
          ))}
          {workOrders.length === 0 && <p className="text-[#475569]">No assigned work orders yet</p>}
        </div>

        <div className="lg:col-span-2 card-pb p-6">
          {!active ? (
            <p className="text-[#475569]">Select a work order to log work</p>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-heading text-2xl text-[#0A2540]">{active.title}</h2>
                  <p className="text-sm text-[#475569] mt-1">{active.customer_code}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/employee/gps?wo=${active.work_order_id}`}
                    className="btn-outline-navy text-sm py-2 px-3"
                    data-testid="track-this-wo"
                  >
                    <MapPin className="w-4 h-4 mr-1" /> Track on map
                  </Link>
                  <button onClick={markComplete} className="btn-accent text-sm" data-testid="mark-complete">Mark Complete</button>
                </div>
              </div>
              <p className="text-[#475569] mb-4">{active.description}</p>

              {active.crew_notes && (
                <div className="bg-amber-50 dark:bg-amber-900/15 border-l-4 border-amber-500 p-3 rounded mb-5" data-testid="crew-notes">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-amber-700 dark:text-amber-400 mb-1">
                    <AlertTriangle className="w-3 h-3" /> Note from admin
                  </div>
                  <p className="text-sm text-amber-900 dark:text-amber-200">{active.crew_notes}</p>
                </div>
              )}

              <MaterialsList workOrderId={active.work_order_id} initial={active.materials_used || []} />

              <div className="card-pb p-4 bg-slate-50 mb-6" data-testid="log-form">
                <h3 className="font-medium text-[#0A2540] mb-3">Log work</h3>
                <textarea
                  value={logText}
                  onChange={(e) => setLogText(e.target.value)}
                  placeholder="What did you work on?"
                  rows="3"
                  data-testid="log-text"
                  className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 text-sm bg-white"
                />
                <div className="mt-3">
                  <PhotoUploader photos={photos} onChange={setPhotos} max={6} label="Site photos" />
                </div>
                <div className="flex flex-wrap gap-3 mt-3 items-center">
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="Hours"
                    data-testid="log-hours"
                    className="border border-[#E2E8F0] rounded-md px-3 py-2 text-sm bg-white w-32"
                  />
                  <button onClick={captureLocation} className="btn-outline-navy text-sm py-2 px-4" data-testid="capture-location">
                    <MapPin className="w-4 h-4 mr-1" /> {pos ? `${pos.lat.toFixed(3)}, ${pos.lng.toFixed(3)}` : "Capture GPS"}
                  </button>
                  <button onClick={submitLog} className="btn-accent text-sm ml-auto" data-testid="submit-log">Submit Log</button>
                </div>
              </div>

              <h3 className="font-heading text-lg text-[#0A2540] mb-3">Previous logs</h3>
              {active.logs?.length > 0 ? (
                <ul className="space-y-3">
                  {active.logs.map((l) => (
                    <li key={l.log_id} className="border-l-2 border-[#C9A356] pl-3 py-1 text-sm">
                      <div className="font-medium text-[#0A2540]">{l.text}</div>
                      <div className="text-xs text-[#475569] mt-1 flex gap-3">
                        <span><Clock className="w-3 h-3 inline" /> {l.hours}h</span>
                        <span>{new Date(l.timestamp).toLocaleString()}</span>
                        {l.lat && <span><MapPin className="w-3 h-3 inline" /> {l.lat.toFixed(3)}, {l.lng.toFixed(3)}</span>}
                      </div>
                      {l.photos?.length > 0 && <PhotoGallery photos={l.photos} />}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#475569]">No logs yet</p>
              )}
            </>
          )}
        </div>
      </div>

      {showSig && active && (
        <SignatureCompleteModal
          workOrderId={active.work_order_id}
          onComplete={() => { setShowSig(false); load(); }}
          onClose={() => setShowSig(false)}
        />
      )}
    </div>
  );
}
